export interface RecentActivityItem {
    type: string;
    title: string;
    description?: string | null;
    occurred_at: string;
    metadata?: Record<string, unknown>;
}

const STORAGE_KEY = "qit:recent-activities";
const MAX_ENTRIES = 25;
export const RECENT_ACTIVITIES_EVENT = "qit:recent-activities-updated";

const normalizeText = (value: unknown, fallback = ""): string => {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
};

const normalizeDateString = (value: unknown): string => {
    const raw = normalizeText(value);
    if (!raw) return new Date().toISOString();

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
    return parsed.toISOString();
};

const normalizeMetadata = (value: unknown): Record<string, unknown> => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }
    return value as Record<string, unknown>;
};

const normalizeActivity = (
    raw: Partial<RecentActivityItem>
): RecentActivityItem | null => {
    const type = normalizeText(raw.type);
    const title = normalizeText(raw.title);
    if (!type || !title) return null;

    return {
        type,
        title,
        description: normalizeText(raw.description, "") || undefined,
        occurred_at: normalizeDateString(raw.occurred_at),
        metadata: normalizeMetadata(raw.metadata),
    };
};

const sortByMostRecent = (items: RecentActivityItem[]): RecentActivityItem[] =>
    [...items].sort((a, b) => {
        const aTime = new Date(a.occurred_at).getTime();
        const bTime = new Date(b.occurred_at).getTime();
        return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
    });

const emitUpdate = (items: RecentActivityItem[]) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent(RECENT_ACTIVITIES_EVENT, {
            detail: { items },
        })
    );
};

export const getRecentActivities = (): RecentActivityItem[] => {
    if (typeof window === "undefined") return [];

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return sortByMostRecent(
            parsed
                .map((entry) => normalizeActivity(entry))
                .filter((entry): entry is RecentActivityItem => Boolean(entry))
        );
    } catch {
        return [];
    }
};

export const setRecentActivities = (
    items: Array<Partial<RecentActivityItem>>
): RecentActivityItem[] => {
    if (typeof window === "undefined") return [];

    const normalized = sortByMostRecent(
        items
            .map((entry) => normalizeActivity(entry))
            .filter((entry): entry is RecentActivityItem => Boolean(entry))
    ).slice(0, MAX_ENTRIES);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    emitUpdate(normalized);
    return normalized;
};

export const clearRecentActivities = (): RecentActivityItem[] => {
    if (typeof window === "undefined") return [];

    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    emitUpdate([]);
    return [];
};

export const addRecentActivity = (
    item: Partial<RecentActivityItem>
): RecentActivityItem[] => {
    const nextItem = normalizeActivity({
        ...item,
        occurred_at: item.occurred_at || new Date().toISOString(),
    });
    if (!nextItem) return getRecentActivities();

    const current = getRecentActivities();
    const duplicate = current.find((entry) => {
        const entrySessionId = normalizeText(entry.metadata?.session_id);
        const nextSessionId = normalizeText(nextItem.metadata?.session_id);
        const entryDedupeKey = normalizeText(entry.metadata?.dedupe_key);
        const nextDedupeKey = normalizeText(nextItem.metadata?.dedupe_key);
        return (
            entry.type === nextItem.type &&
            entry.title === nextItem.title &&
            entry.description === nextItem.description &&
            entrySessionId === nextSessionId &&
            entryDedupeKey === nextDedupeKey &&
            Math.abs(
                new Date(entry.occurred_at).getTime() - new Date(nextItem.occurred_at).getTime()
            ) < 5000
        );
    });

    if (duplicate) return current;
    return setRecentActivities([nextItem, ...current]);
};

export const addSessionActivity = (params: {
    action: "started" | "joined" | "left" | "ended";
    sessionId?: string;
    playlistId?: string;
    sessionTitle?: string;
    actor?: string;
    reason?: string;
}) => {
    const sessionTitle = normalizeText(params.sessionTitle, "Live Session");
    const actor = normalizeText(params.actor);
    const reason = normalizeText(params.reason);

    const activityByAction: Record<
        "started" | "joined" | "left" | "ended",
        { type: string; title: string; description: string }
    > = {
        started: {
            type: "session_started",
            title: "Started a live session",
            description: `Started "${sessionTitle}".`,
        },
        joined: {
            type: "session_joined",
            title: "Joined a live session",
            description: `Joined "${sessionTitle}".`,
        },
        left: {
            type: "session_left",
            title: "Left a live session",
            description: `Left "${sessionTitle}".`,
        },
        ended: {
            type: "session_ended",
            title: "Ended a live session",
            description: `Ended "${sessionTitle}".`,
        },
    };

    const activity = activityByAction[params.action];
    let description = activity.description;

    if (params.action === "ended" && actor && actor.toLowerCase() !== "you") {
        description = `${actor} ended "${sessionTitle}".`;
    }
    if (params.action === "left" && actor && actor.toLowerCase() !== "you") {
        description = `${actor} left "${sessionTitle}".`;
    }
    if (reason) {
        description = `${description} ${reason}`;
    }

    return addRecentActivity({
        type: activity.type,
        title: activity.title,
        description,
        metadata: {
            session_id: normalizeText(params.sessionId),
            playlist_id: normalizeText(params.playlistId),
            session_title: sessionTitle,
            actor: actor || undefined,
            dedupe_key: `${params.action}:${normalizeText(params.sessionId)}:${sessionTitle}`,
        },
    });
};

export const addSessionSongActivity = (params: {
    action: "added" | "suggested";
    sessionId?: string;
    playlistId?: string;
    sessionTitle?: string;
    songId?: string;
    songTitle?: string;
    actor?: string;
}) => {
    const sessionTitle = normalizeText(params.sessionTitle, "Live Session");
    const songTitle = normalizeText(params.songTitle, "this song");
    const actor = normalizeText(params.actor, "You");
    const isAdded = params.action === "added";

    return addRecentActivity({
        type: isAdded ? "session_song_added" : "session_song_suggested",
        title: isAdded ? "Added a song to a session" : "Suggested a song in a session",
        description: `${actor} ${isAdded ? "added" : "suggested"} "${songTitle}" in "${sessionTitle}".`,
        metadata: {
            session_id: normalizeText(params.sessionId),
            playlist_id: normalizeText(params.playlistId),
            session_title: sessionTitle,
            song_id: normalizeText(params.songId),
            song_title: songTitle,
            actor,
            dedupe_key: `${params.action}:${normalizeText(params.sessionId)}:${normalizeText(params.songId)}:${Date.now()}`,
        },
    });
};

export const addSessionVoteActivity = (params: {
    sessionId?: string;
    playlistId?: string;
    sessionTitle?: string;
    songId?: string;
    songTitle?: string;
    voteAmount: number;
}) => {
    const sessionTitle = normalizeText(params.sessionTitle, "Live Session");
    const songTitle = normalizeText(params.songTitle, "this song");
    const voteAmount = Number.isFinite(params.voteAmount) ? Math.max(1, Math.floor(params.voteAmount)) : 1;

    return addRecentActivity({
        type: "session_vote_cast",
        title: "Voted in a live session",
        description: `Voted ${voteAmount} on "${songTitle}" in "${sessionTitle}".`,
        metadata: {
            session_id: normalizeText(params.sessionId),
            playlist_id: normalizeText(params.playlistId),
            session_title: sessionTitle,
            song_id: normalizeText(params.songId),
            song_title: songTitle,
            vote_amount: voteAmount,
            dedupe_key: `vote:${normalizeText(params.sessionId)}:${normalizeText(params.songId)}:${voteAmount}:${Date.now()}`,
        },
    });
};
